import React from 'react'
import { motion } from 'framer-motion'
import { ChatMessage as ChatMessageType } from '../../services/chatService'

interface ChatMessageProps {
  message: ChatMessageType
  isOwn: boolean
  showTimestamp?: boolean
  className?: string
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ 
  message, 
  isOwn, 
  showTimestamp = true,
  className = ''
}) => {
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false
    })
  }

  const getStatusIcon = (status: ChatMessageType['status']) => {
    switch (status) {
      case 'sent':
        return (
          <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )
      case 'delivered':
        return (
          <div className="flex space-x-0.5">
            <svg className="w-3 h-3 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <svg className="w-3 h-3 text-gray-400 -ml-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )
      case 'read':
        return (
          <div className="flex space-x-0.5">
            <svg className="w-3 h-3 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            <svg className="w-3 h-3 text-blue-500 -ml-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </div>
        )
      case 'failed':
        return (
          <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        )
      default:
        return null
    }
  }

  const messageVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={messageVariants}
      className={`flex ${isOwn ? 'justify-end' : 'justify-start'} mb-4 ${className}`}
    >
      <div className={`max-w-xs lg:max-w-md ${isOwn ? 'order-2' : 'order-1'}`}>
        {/* Message bubble */}
        <div
          className={`
            px-4 py-2 rounded-2xl shadow-sm
            ${isOwn 
              ? 'bg-blue-500 text-white rounded-br-md' 
              : 'bg-gray-100 text-gray-900 rounded-bl-md'
            }
            ${message.message_type === 'template' ? 'border border-gray-300' : ''}
          `}
        >
          {/* Message content */}
          <div className="text-sm leading-relaxed">
            {message.content}
          </div>

          {/* Message type indicator for special messages */}
          {message.message_type !== 'text' && (
            <div className={`
              text-xs mt-1 opacity-75
              ${isOwn ? 'text-blue-100' : 'text-gray-500'}
            `}>
              {message.message_type === 'template' && '📝 Template'}
              {message.message_type === 'interactive' && '🔘 Interactive'}
              {message.message_type === 'image' && '🖼️ Image'}
              {message.message_type === 'document' && '📄 Document'}
            </div>
          )}
        </div>

        {/* Timestamp and status */}
        {showTimestamp && (
          <div className={`
            flex items-center mt-1 px-2
            ${isOwn ? 'justify-end space-x-2' : 'justify-start'}
          `}>
            <span className="text-xs text-gray-500">
              {formatTimestamp(message.created_at)}
            </span>
            {isOwn && (
              <div className="flex items-center">
                {getStatusIcon(message.status)}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  )
}

export default ChatMessage